import type { Metadata } from "next";

const SITE_NAME = "steampeek";
const DEFAULT_BASE_URL = "https://steampeek.app";

/**
 * Отримує базовий URL сайту з env або використовує дефолтний
 */
export function getBaseUrl(): string {
  return process.env.NEXT_PUBLIC_SITE_URL || DEFAULT_BASE_URL;
}

export function getSteamImageUrl(
  imagePath: string | null | undefined,
  appid?: number
): string | undefined {
  if (!imagePath) return undefined;
  if (imagePath.startsWith("http")) return imagePath;
  if (appid) return `https://cdn.akamai.steamstatic.com/steam/apps/${appid}/${imagePath}`;
  return undefined;
}

/**
 * Створює базові metadata з Open Graph та Twitter картками
 */
export function createMetadata({
  title,
  description,
  url,
  image,
  imageAlt,
}: {
  title: string;
  description: string;
  url?: string;
  image?: string;
  imageAlt?: string;
}): Metadata {
  // Don't append the suffix when the title already says the brand. The
  // homepage reads "steampeek — the full tour of every Steam number"; with a
  // blanket suffix a Telegram/Slack card printed "steampeek" (og:site_name),
  // then "steampeek — … | steampeek" — the name three times in four lines.
  const alreadyBranded =
    title.includes("|") || title.toLowerCase().includes(SITE_NAME);
  const fullTitle = alreadyBranded ? title : `${title} | ${SITE_NAME}`;
  const pageUrl = url ? `${getBaseUrl()}${url}` : undefined;

  // ⚠️ When there is no page-specific image the `images` key is OMITTED, not
  // set to []. An explicit empty array overrides the file-based
  // `app/opengraph-image.tsx` convention, which would leave every list page
  // back where it started: a link preview with no picture.
  const ogImage = image
    ? [{ url: image, width: 1200, height: 630, alt: imageAlt || title }]
    : undefined;

  return {
    // `absolute` opts out of the root layout's `%s | steampeek` template —
    // otherwise the suffix stacks and every page renders "X | steampeek |
    // steampeek" (was live on prod for all createMetadata pages).
    title: { absolute: fullTitle },
    description,
    openGraph: {
      title: fullTitle,
      description,
      type: "website",
      siteName: SITE_NAME,
      ...(ogImage && { images: ogImage }),
      url: pageUrl,
    },
    twitter: {
      card: "summary_large_image",
      title: fullTitle,
      description,
      ...(image && { images: [image] }),
    },
    ...(pageUrl && {
      alternates: {
        canonical: pageUrl,
      },
    }),
  };
}

/**
 * Створює metadata для сторінок списків (rankings, listings)
 */
export function createListPageMetadata({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}): Metadata {
  return createMetadata({
    title,
    description,
    url: path,
  });
}

/**
 * Створює metadata для сторінки "Not Found"
 */
export function createNotFoundMetadata(
  entity: string = "Page"
): Metadata {
  return {
    title: `${entity} Not Found | ${SITE_NAME}`,
    description: `The requested ${entity.toLowerCase()} could not be found.`,
  };
}
